import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { fetchJSON } from "@/lib/api";
import { fetchActiveFuelProducts, fuelProductLabel, parseLfrPrice, type FuelProductRow } from "@/lib/fuelProducts";
import { useFuelRates } from "@/hooks/useFuelRates";
import { ArrowRight, Edit3 } from "lucide-react";

type DailyRateSummary = {
  fuelProductId: string;
  productName: string;
  rate: string;
};

const today = () => new Date().toISOString().slice(0, 10);

const productDefaultRate = (product: FuelProductRow) => {
  const parsed = parseLfrPrice(product);
  if (parsed !== undefined) return String(parsed);
  const price = Number((product as any).price ?? (product as any).saleRate ?? (product as any).sale_rate ?? 0);
  return Number.isFinite(price) && price > 0 ? String(price) : "";
};

const displayRate = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n.toFixed(2);
};

export default function ShiftSheetEntry() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { organizationName } = useAuth();
  const [entryDate, setEntryDate] = useState(today());
  const [selectedDate, setSelectedDate] = useState("");
  const [rates, setRates] = useState<DailyRateSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fallback rates from hook (daily-sale-rates → LFR → history)
  const { getRateByCode } = useFuelRates({ date: entryDate, includeSalesHistory: true });

  const loadDailyRates = async (date: string) => {
    setIsLoading(true);
    try {
      const products = await fetchActiveFuelProducts();
      const response = await fetchJSON(`/api/daily-rates?date=${encodeURIComponent(date)}`);
      const result = await response.json();
      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || "Failed to load daily rates");
      }

      const existingRates = (result.data || result.rows || []) as any[];
      const nextRates = products.map((product) => {
        const productId = String(product.id);
        const label = fuelProductLabel(product);
        const savedById = existingRates.find(
          (rate) => String(rate.fuelProductId || rate.fuel_product_id || "") === productId
        );
        const saved =
          savedById ||
          existingRates.find((rate) => {
            const savedName = String(rate.fuelProduct || rate.fuel_product || rate.product_name || "").toUpperCase();
            return savedName && savedName === label.toUpperCase();
          });

        // Use existing rate, else fall back to the hook's resolved rate
        const rateFromApi =
          displayRate(saved?.closeRate ?? saved?.close_rate) ||
          displayRate(saved?.openRate ?? saved?.open_rate) ||
          displayRate(productDefaultRate(product));

        const fallback = !rateFromApi || rateFromApi === "-"
          ? (getRateByCode(label) != null ? String(getRateByCode(label)!.toFixed(3)) : "")
          : "";

        return {
          fuelProductId: productId,
          productName: label,
          rate: rateFromApi || fallback || "-",
        };
      });

      setRates(nextRates);
      setSelectedDate(date);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Unable to load rates",
        description: error?.message || "Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDate = () => {
    if (!entryDate) {
      toast({
        variant: "destructive",
        title: "Choose date",
        description: "Please choose a date before selecting.",
      });
      return;
    }

    loadDailyRates(entryDate);
  };

  const goToDailyRates = () => {
    if (!selectedDate) return;
    navigate(`/day-business/daily-sale-rate?date=${encodeURIComponent(selectedDate)}&fromShiftSheet=1`);
  };

  const goToSaleEntry = () => {
    if (!selectedDate) return;
    navigate(`/day-business/sale-entry?date=${encodeURIComponent(selectedDate)}`);
  };

  return (
    <div className="min-h-full bg-white px-4 pb-10 pt-2">
      <h1 className="text-center text-2xl font-bold uppercase text-teal-700 drop-shadow-sm">
        {organizationName || "Service Centre"}
      </h1>

      <section className="mx-auto mt-10 max-w-5xl rounded-md bg-[#4c61c9] px-8 py-10 shadow-md">
        <div className="mx-auto grid max-w-[600px] grid-cols-1 gap-1 sm:grid-cols-[220px_1fr_72px]">
          <button
            type="button"
            className="h-9 bg-[#ffd35a] px-4 text-xs font-bold text-slate-900"
            onClick={() => document.getElementById("shift-sheet-date")?.click()}
          >
            Choose Date
          </button>
          <Input
            id="shift-sheet-date"
            type="date"
            value={entryDate}
            onChange={(event) => setEntryDate(event.target.value)}
            className="h-9 rounded-none border-0 bg-white text-xs text-slate-900"
          />
          <Button
            type="button"
            className="h-9 rounded bg-cyan-500 px-4 text-xs font-bold text-white hover:bg-cyan-600"
            onClick={handleSelectDate}
            disabled={isLoading}
          >
            {isLoading ? "..." : "Select"}
          </Button>
        </div>

        {selectedDate && (
          <div className="mt-5">
            <div className="rounded border border-white/80 px-6 py-7">
              {rates.length === 0 ? (
                <div className="text-center text-sm font-semibold text-white">
                  Add fuel products in Master &gt; Fuel Products first.
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
                  <div className="text-lg font-bold text-yellow-300">Rates :</div>
                  {rates.map((rate) => (
                    <div key={rate.fuelProductId} className="flex items-center gap-4">
                      <span className="text-sm font-bold uppercase text-white">{rate.productName}</span>
                      <span className="min-w-14 rounded-sm bg-[#6d8791] px-4 py-2 text-center text-xs font-bold text-white">
                        {rate.rate}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-1">
              <Button
                type="button"
                onClick={goToDailyRates}
                className="h-10 rounded-sm bg-orange-600 px-4 text-xs font-bold text-white hover:bg-orange-700"
              >
                <Edit3 className="mr-1 h-4 w-4" />
                EDIT RATES
              </Button>
              <Button
                type="button"
                onClick={goToSaleEntry}
                disabled={rates.length === 0}
                className="h-10 rounded-sm bg-[#52c653] px-4 text-xs font-bold text-white hover:bg-[#44b747]"
              >
                <ArrowRight className="mr-1 h-4 w-4" />
                GO TO ENTRY PAGE
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

